import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './HistoryModal.scss';
import { ApiAssignmentUser } from '../../../api';
import { SalvButton, SalvModal, SalvProgressStepper, Size, Step } from '@salvapp/ui-components';
import { formatDate } from '../../datetime/FormattedDateTime';
import { InternalApiAlertType, InternalApiDocumentEvent } from '../../../api-internal';
import { alertEventsApi } from '../../../apis';
import { Spinner } from '../../../feedback/Spinner';
import { useMe } from '../../../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';

interface Props {
  loadEvents: () => Promise<HistoryEvent[]>;
  dropdownItem?: boolean;
  alertId: number;
  alertType: InternalApiAlertType;
}

export interface HistoryEvent {
  type: HistoryEventType;
  startTime: Date;
  assigner?: Omit<ApiAssignmentUser, 'id'>;
  assignee?: Omit<ApiAssignmentUser, 'id'>;
  status?: string;
  comment?: string;
  documentEvent?: InternalApiDocumentEvent;
}

export enum HistoryEventType {
  STATUS,
  ASSIGNMENT,
  NOTE,
  IS_RE_SCREENING_ENABLED,
  DOCUMENT_DELETED,
}

export const HistoryModalWithToggle = ({ loadEvents, dropdownItem, alertId, alertType }: Props) => {
  const [showModal, setShowModal] = useState(false);

  const [events, setEvents] = useState<HistoryEvent[] | null>(null);
  const toggleModal = useCallback((isOpen: boolean) => {
    if (isOpen) {
      loadEvents().then(setEvents);
    } else {
      setEvents(null);
    }
    setShowModal(isOpen);
  }, []);

  return (
    <>
      {dropdownItem ? (
        <SalvButton
          onClick={() => {
            toggleModal(true);
          }}
          variant='muted'
          label='View history'
          className='dropdown-item'
        />
      ) : (
        <SalvButton
          onClick={() => {
            toggleModal(true);
          }}
          size={Size.SMALL}
          variant='muted'
          label='History'
        />
      )}
      <HistoryModal
        showModal={showModal}
        setShowModal={toggleModal}
        events={events}
        alertId={alertId}
        alertType={alertType}
      />
    </>
  );
};

interface ModalProps {
  alertId: number;
  alertType: InternalApiAlertType;
  showModal: boolean;
  setShowModal: (arg0: boolean) => void;
  events: HistoryEvent[] | null;
}

export const HistoryModal = ({ showModal, setShowModal, events: propsEvents, alertId, alertType }: ModalProps) => {
  const [alertEvents, setAlertEvents] = useState<HistoryEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { features } = useMe();
  const documentDeletionHistoryEnabled = isFeatureEnabled(ORGANISATION_FEATURE.DOCUMENT_DELETION_HISTORY, features);
  useEffect(() => {
    if (showModal && alertId && alertType) {
      setLoading(true);
      setAlertEvents([]);
      setError(null);
      alertEventsApi
        .getAlertEvents({ alertId, alertType })
        .then((res) => {
          const reScreeningEvents = res.isReScreeningEnabledEvents.map((event) => {
            return {
              type: HistoryEventType.IS_RE_SCREENING_ENABLED,
              startTime: event.createdTime,
              assignee: event.createdBy,
              comment: `${event.createdBy?.name ? event.createdBy.name : 'System'} has ${event.isReScreeningEnabled ? 'enabled' : 'disabled'} re-screening`,
            };
          });
          const documentEvents = documentDeletionHistoryEnabled
            ? (res.documentEvents || []).map((event) => {
                return {
                  type: HistoryEventType.DOCUMENT_DELETED,
                  startTime: event.deletedTime || event.createdTime,
                  assigner: event.deletedBy || event.createdBy,
                  documentEvent: {
                    fileName: event.fileName,
                    createdTime: event.createdTime,
                    deletedTime: event.deletedTime,
                    createdBy: event.createdBy,
                    deletedBy: event.deletedBy,
                  },
                };
              })
            : [];
          setAlertEvents([...reScreeningEvents, ...documentEvents]);
        })
        .catch(() => {
          setError('Failed to load alert events. Please try again.');
          setAlertEvents([]);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setAlertEvents([]);
      setLoading(false);
      setError(null);
    }
  }, [alertId, alertType, showModal, documentDeletionHistoryEnabled]);
  const events = useMemo(
    () => (propsEvents || []).concat(alertEvents).sort((a, b) => b.startTime.getTime() - a.startTime.getTime()),
    [propsEvents, alertEvents]
  );
  return (
    <SalvModal show={showModal} onHide={() => setShowModal(false)}>
      <SalvModal.Header closeButton>Alert history</SalvModal.Header>

      <SalvModal.Body>
        {loading && <Spinner loading={true} small={true} />}
        {error && (
          <div role='alert' aria-live='polite' style={{ textAlign: 'center', padding: '16px', color: 'var(--error)' }}>
            {error}
          </div>
        )}
        {!loading && !error && events && events.length > 0 && (
          <SalvProgressStepper activeStep={1}>
            {events.map((event, index) => {
              switch (event.type) {
                case HistoryEventType.ASSIGNMENT: {
                  return (
                    <Step
                      key={index}
                      header={`${event.assigner && event.assigner.name} assigned to: ${
                        event.assignee && event.assignee.name
                      }`}
                      description={formatDate(event.startTime, true)}
                    />
                  );
                }
                case HistoryEventType.STATUS: {
                  return (
                    <Step
                      key={index}
                      header={
                        event.assigner
                          ? `${event.assigner.name} set status to: ${event.status}`
                          : index == events.length - 1
                            ? `Initial status: ${event.status}`
                            : `Status changed to: ${event.status}`
                      }
                      description={formatDate(event.startTime, true)}
                    >
                      {event.comment && (
                        <div className={'m-t-12'}>
                          <div className={'text-xs'} style={{ color: 'var(--text-medium)' }}>
                            Note
                          </div>
                          <div className={'text-s'}>{event.comment}</div>
                        </div>
                      )}
                    </Step>
                  );
                }
                case HistoryEventType.NOTE: {
                  return (
                    <Step
                      key={index}
                      header={`${event.assigner && event.assigner.name} added a note`}
                      description={formatDate(event.startTime, true)}
                    >
                      <div className={'m-t-12'}>
                        <div className={'text-xs'} style={{ color: 'var(--text-medium)' }}>
                          Note
                        </div>
                        <div className={'text-s'}>{event.comment}</div>
                      </div>
                    </Step>
                  );
                }
                case HistoryEventType.IS_RE_SCREENING_ENABLED: {
                  return <Step key={index} header={event.comment!} description={formatDate(event.startTime, true)} />;
                }
                case HistoryEventType.DOCUMENT_DELETED: {
                  const action = 'removed file';
                  const fileName = event.documentEvent?.fileName || 'unknown file';
                  const user = event.documentEvent?.deletedBy?.name || event.documentEvent?.createdBy?.name || 'System';
                  const deletedTime = event.documentEvent?.deletedTime || event.startTime;
                  const uploadedBy = event.documentEvent?.createdBy?.name;
                  const uploadedTime = event.documentEvent?.createdTime;
                  return (
                    <Step
                      key={index}
                      header={`${user} ${action}: ${fileName}`}
                      description={formatDate(deletedTime, true)}
                    >
                      {uploadedBy && uploadedTime && (
                        <div className={'m-t-12'}>
                          <div className={'text-xs'} style={{ color: 'var(--text-medium)' }}>
                            Uploaded by {uploadedBy} on {formatDate(uploadedTime, true)}
                          </div>
                        </div>
                      )}
                      {!uploadedBy && uploadedTime && (
                        <div className={'m-t-12'}>
                          <div className={'text-xs'} style={{ color: 'var(--text-medium)' }}>
                            Uploaded on {formatDate(uploadedTime, true)}
                          </div>
                        </div>
                      )}
                    </Step>
                  );
                }
              }
            })}
          </SalvProgressStepper>
        )}
        {!loading && !error && propsEvents !== null && events && events.length === 0 && (
          <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-medium)' }}>No history events</div>
        )}
      </SalvModal.Body>

      <SalvModal.Footer>
        <SalvButton label={'Close'} variant='muted' onClick={() => setShowModal(false)} />
      </SalvModal.Footer>
    </SalvModal>
  );
};
